import { splitInstanceBasePath } from "@/lib/dashboard-routes";
import type { UrlObject } from "url";

export const DEFAULT_GITHUB_INSTANCE_SLUG = "github";

export interface NamedOrg {
  orgName: string;
  instanceSlug: string;
}

export function orgApiPath({ orgName, instanceSlug }: NamedOrg): string {
  return `/user/${encodeURIComponent(instanceSlug)}/orgs/${encodeURIComponent(
    orgName,
  )}`;
}

/**
 * The GitHub instance a dashboard URL addresses: `/enterprise/ghe/acme/runs`
 * → `"ghe"`; anything without the prefix is the account default, github.com
 * unless told otherwise.
 */
export function urlInstanceSlug(
  pathname?: string,
  defaultInstanceSlug?: string | null,
): string {
  const fallback = defaultInstanceSlug || DEFAULT_GITHUB_INSTANCE_SLUG;
  const path =
    pathname ??
    (typeof window === "undefined" ? undefined : window.location.pathname);
  if (path === undefined) return fallback;
  return splitInstanceBasePath(path).instanceSlug ?? fallback;
}

export function instanceBasePath(instanceSlug?: string | null): string {
  return !instanceSlug || instanceSlug === DEFAULT_GITHUB_INSTANCE_SLUG
    ? ""
    : `/enterprise/${encodeURIComponent(instanceSlug)}`;
}

/**
 * Dashboard hrefs are written for github.com (`/acme/runs`); on an enterprise
 * instance the same href lives under its base path, as with Next's `basePath`:
 * `withInstanceBasePath("/acme/runs", "ghe")` → `/enterprise/ghe/acme/runs`.
 * Absolute URLs, relative hrefs and hrefs already under a base path pass through.
 */
export function withInstanceBasePath(
  href: string,
  instanceSlug?: string | null,
): string {
  const base = instanceBasePath(instanceSlug);
  if (!base || !href.startsWith("/") || href.startsWith("//")) return href;
  if (splitInstanceBasePath(href).instanceSlug !== null) return href;
  const isRoot = href === "/" || href.startsWith("/?") || href.startsWith("/#");
  return `${base}${isRoot ? href.slice(1) : href}`;
}

/** `returnTo` is the dashboard path to land on once the install completes. */
export function githubAppInstallUrl(
  instanceSlug?: string | null,
  returnTo?: string,
): string {
  const slug = instanceSlug || DEFAULT_GITHUB_INSTANCE_SLUG;
  const url = `${process.env.NEXT_PUBLIC_API_URL}/${encodeURIComponent(
    slug,
  )}/install`;
  return returnTo
    ? `${url}?${new URLSearchParams({ return_to: returnTo })}`
    : url;
}

/** Signs out and lands on the sign-in page of the instance the URL names. */
export function logoutUrl(instanceSlug?: string | null): string {
  const api = process.env.NEXT_PUBLIC_API_URL;
  return !instanceSlug || instanceSlug === DEFAULT_GITHUB_INSTANCE_SLUG
    ? `${api}/logout`
    : `${api}/enterprise/${encodeURIComponent(instanceSlug)}/logout`;
}

const GITHUB_ORG_PATH = /^(.*\/)?user\/github\/orgs\//;
const INSTANCE_SCOPED_ASSISTANT_PATH =
  /^(.*\/)?assistant\/(sessions(?:\/(?:semantic-search|lookup))?|floor|resolve-pr|pr-session-status|resolve-linear-issue|dictation\/(?:cleanup|transcribe|realtime-session|realtime-usage))(?=[?#]|$)/;
const INSTANCE_ASSISTANT_PATH = /(^|\/)enterprise\/[^/]+\/assistant\//;

/**
 * Points github.com-shaped API paths at the instance the dashboard URL
 * addresses: `user/github/orgs/acme` → `user/ghe/orgs/acme` and
 * `assistant/sessions` → `enterprise/ghe/assistant/sessions`.
 */
export function scopeOrgApiPath(
  path: string,
  defaultInstanceSlug?: string | null,
): string {
  const slug = urlInstanceSlug(undefined, defaultInstanceSlug);
  if (slug === DEFAULT_GITHUB_INSTANCE_SLUG) return path;
  const encoded = encodeURIComponent(slug);
  if (GITHUB_ORG_PATH.test(path)) {
    return path.replace(GITHUB_ORG_PATH, `$1user/${encoded}/orgs/`);
  }
  if (INSTANCE_ASSISTANT_PATH.test(path)) return path;
  return path.replace(
    INSTANCE_SCOPED_ASSISTANT_PATH,
    `$1enterprise/${encoded}/assistant/$2`,
  );
}

function appendQueryValue(
  searchParams: URLSearchParams,
  key: string,
  value: unknown,
) {
  if (Array.isArray(value)) {
    value.forEach((entry) => appendQueryValue(searchParams, key, entry));
  } else if (value !== undefined && value !== null) {
    searchParams.append(key, String(value));
  }
}

export function serializeDashboardUrl(url: string | UrlObject): string {
  if (typeof url === "string") return url;

  const query =
    url.query && typeof url.query === "object" ? { ...url.query } : {};
  let pathname = url.pathname ?? "";
  pathname = pathname.replace(
    /\/\[\[\.\.\.([^\]]+)\]\]|\/\[\.\.\.([^\]]+)\]|\/\[([^\]]+)\]/g,
    (token, optionalCatchAll, catchAll, dynamic) => {
      const key = optionalCatchAll ?? catchAll ?? dynamic;
      const value = query[key];
      delete query[key];
      if (value === undefined || value === null || value === "") {
        return optionalCatchAll ? "" : token;
      }
      const parts = Array.isArray(value) ? value : [value];
      return `/${parts
        .map((part) => encodeURIComponent(String(part)))
        .join("/")}`;
    },
  );

  const searchParams = new URLSearchParams(url.search ?? "");
  Object.entries(query).forEach(([key, value]) =>
    appendQueryValue(searchParams, key, value),
  );
  const search = searchParams.toString();
  const hash = url.hash
    ? url.hash.startsWith("#")
      ? url.hash
      : `#${url.hash}`
    : "";
  return `${pathname}${search ? `?${search}` : ""}${hash}`;
}

/**
 * Keeps navigation on the enterprise instance the current URL addresses:
 * from `/enterprise/ghe/acme/runs`, `push("/acme/sessions")` goes to
 * `/enterprise/ghe/acme/sessions`. Hrefs are written for github.com, as with
 * Next's `basePath`.
 */
export function scopeToInstanceBasePath(
  url: string | UrlObject,
  currentPathname: string,
): string {
  return withInstanceBasePath(
    serializeDashboardUrl(url),
    splitInstanceBasePath(currentPathname).instanceSlug,
  );
}
